import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Loader2,
  Pencil,
  Plug,
  Plus,
  Search,
  Users,
  X,
} from "lucide-react";

import { ApiError, formatDate } from "../../lib/api";
import { useDebounced } from "../../lib/debounce";
import { errorRut, formatRut, normalizarRut } from "../../lib/rut";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { AuditInfo } from "../../components/AuditInfo";
import { Modal } from "../../components/Modal";
import {
  Badge,
  type BadgeTone,
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  useDescarteProtegido,
  type OrigenNavegacion,
} from "../../components/ui";
import {
  escribirFiltros,
  leerFiltros,
  rangoMostrado,
  TAMANO_PAGINA,
  totalPaginas,
  type FiltrosSocios,
} from "./filtrosSocios";
import { PortalSocioModal } from "./PortalSocioModal";
import {
  actualizarSocio,
  crearSocio,
  fetchContratos,
  paginaSocios,
  type CondicionFiltro,
  type EstadoFiltro,
  type EstadoSocio,
  type SocioCreatePayload,
  type SocioRead,
  type SocioUpdatePayload,
} from "./api";

export function SociosListPage(): JSX.Element {
  // Consulta, filtros y página viven en la URL (auditoría UX-07): volver
  // desde la cuenta corriente deja el listado como estaba.
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const filtros = leerFiltros(params);
  const [texto, setTexto] = useState(filtros.q);
  const [editing, setEditing] = useState<SocioRead | "new" | null>(null);
  // Socio recién creado: se ofrece el paso siguiente (auditoría UX-12).
  const [recienCreado, setRecienCreado] = useState<SocioRead | null>(null);
  const inicioListaRef = useRef<HTMLDivElement>(null);

  const actualizarFiltros = useCallback(
    (cambio: Partial<FiltrosSocios>, opciones?: { reemplazar?: boolean }): void => {
      setParams(
        (prev) => {
          const actual = leerFiltros(prev);
          // Cambiar la consulta o un filtro vuelve a la página 1: la página 7
          // de otra búsqueda puede no existir.
          const pagina = cambio.pagina ?? 1;
          return escribirFiltros({ ...actual, ...cambio, pagina });
        },
        // Tipear no llena el historial; cambiar de página sí (Atrás vuelve).
        { replace: opciones?.reemplazar ?? cambio.pagina === undefined },
      );
    },
    [setParams],
  );

  const textoDebounced = useDebounced(texto, 300);
  // Última consulta que escribimos nosotros en la URL. Si la URL cambia por
  // otra vía (Atrás / Adelante), el texto del buscador la sigue (auditoría
  // UX-03); antes el debounce volvía a escribir la búsqueda vieja.
  const qPropiaRef = useRef(filtros.q);
  // Lo tipeado se escribe en la URL sólo cuando cambia el texto (ya
  // asentado), no cuando cambia la URL: si no, al volver con Atrás el
  // debounce reescribía la búsqueda vieja encima de la del historial.
  const ultimoDebouncedRef = useRef(textoDebounced);
  useEffect(() => {
    if (textoDebounced === ultimoDebouncedRef.current) return;
    ultimoDebouncedRef.current = textoDebounced;
    if (textoDebounced.trim() !== filtros.q) {
      qPropiaRef.current = textoDebounced.trim();
      actualizarFiltros({ q: textoDebounced });
    }
  }, [textoDebounced, filtros.q, actualizarFiltros]);
  useEffect(() => {
    if (filtros.q === qPropiaRef.current) return;
    qPropiaRef.current = filtros.q;
    setTexto(filtros.q);
  }, [filtros.q]);

  const filtroActivo = filtros.q;
  const offset = (filtros.pagina - 1) * TAMANO_PAGINA;

  const sociosQ = useQuery({
    queryKey: [
      "socios",
      "pagina",
      { condicion: filtros.condicion, estado: filtros.estado, q: filtroActivo, offset },
    ],
    queryFn: () =>
      paginaSocios({
        condicion: filtros.condicion,
        estado: filtros.estado,
        search: filtroActivo || null,
        limit: TAMANO_PAGINA,
        offset,
      }),
    placeholderData: (prev) => prev,
  });

  const sociosVisibles = sociosQ.data?.items ?? [];
  const total = sociosQ.data?.total ?? 0;
  const paginas = totalPaginas(total);
  // Una página fuera de rango (URL vieja, o el total bajó al cambiar un
  // estado) vuelve a la última que existe (auditoría UX-04).
  const fueraDeRango =
    sociosQ.isSuccess && !sociosQ.isPlaceholderData && total > 0 && filtros.pagina > paginas;
  useEffect(() => {
    if (fueraDeRango) actualizarFiltros({ pagina: paginas }, { reemplazar: true });
  }, [fueraDeRango, paginas, actualizarFiltros]);
  const buscando = texto.trim() !== filtroActivo || (sociosQ.isFetching && !!filtroActivo);
  const origen = { etiqueta: "Socios", ruta: `${location.pathname}${location.search}` };
  const hayFiltros = filtros.condicion !== "todos" || filtros.estado !== "activos";

  function irAPagina(n: number): void {
    actualizarFiltros({ pagina: n });
    inicioListaRef.current?.scrollIntoView({ block: "start" });
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Socios"
        icon={Users}
        subtitle={
          sociosQ.data
            ? `${total.toLocaleString("es-CL")} ${
                filtroActivo || hayFiltros ? "resultados" : "en el padrón"
              }`
            : undefined
        }
        actions={
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nuevo socio
          </Button>
        }
      />

      {editing && (
        <SocioForm
          socio={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onCreated={setRecienCreado}
        />
      )}

      {recienCreado && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <strong>{recienCreado.nombre}</strong> quedó en el padrón
            {recienCreado.numero_socio != null && <> con el N° {recienCreado.numero_socio}</>}.
            Siguiente paso: conectarlo a la red para que reciba agua y boleta.
          </span>
          <Link
            to={`/contratos?conexion=nueva&socio=${recienCreado.id}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-brand-800"
          >
            <Plug className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
            Conectar a la red
          </Link>
          <Link
            to={`/socios/${recienCreado.id}/cuenta-corriente`}
            state={{ desde: origen }}
            className="text-sm font-medium text-brand-700 hover:text-brand-800"
          >
            Ver su cuenta corriente
          </Link>
          <button
            type="button"
            onClick={() => setRecienCreado(null)}
            className="grid h-7 w-7 place-items-center rounded-md text-emerald-700 hover:bg-emerald-100"
            aria-label="Cerrar aviso"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-neutral-200 p-3 space-y-3">
        {/* Búsqueda con etiqueta visible y ayuda asociada (auditoría UX-06). */}
        <div>
          <label htmlFor="buscar-socios" className="block text-sm font-medium text-neutral-700">
            Buscar socios
          </label>
          <div className="relative mt-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <input
              id="buscar-socios"
              type="search"
              placeholder="Nombre, RUT o N° de socio"
              aria-describedby="buscar-socios-ayuda"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              className="w-full pl-9 pr-20 py-2 rounded-md border border-neutral-450 text-sm shadow-sm placeholder:text-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {buscando && (
                <Loader2
                  className="h-4 w-4 text-neutral-500 animate-spin"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
              )}
              {texto && (
                <button
                  type="button"
                  onClick={() => {
                    setTexto("");
                    actualizarFiltros({ q: "" });
                  }}
                  className="grid h-9 w-9 place-items-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
                  aria-label="Limpiar búsqueda"
                  title="Limpiar búsqueda"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
          <p id="buscar-socios-ayuda" className="mt-1 text-xs text-neutral-600">
            También busca por apellido, N° de libro, teléfono y correo. El RUT va con o sin
            puntos. Separa palabras con espacio para combinarlas (ej. «juan perez»).
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-xl">
          <Select
            label="Condición"
            value={filtros.condicion}
            onChange={(e) => actualizarFiltros({ condicion: e.target.value as CondicionFiltro })}
          >
            <option value="todos">Socios y clientes</option>
            <option value="socios">Solo socios</option>
            <option value="clientes">Solo clientes</option>
          </Select>
          <Select
            label="Estado"
            value={filtros.estado}
            onChange={(e) => actualizarFiltros({ estado: e.target.value as EstadoFiltro })}
          >
            <option value="activos">Activos</option>
            <option value="Inactivo">Inactivos</option>
            <option value="Fallecido">Fallecidos</option>
            <option value="todos">Todos</option>
          </Select>
        </div>
      </div>

      <div ref={inicioListaRef} className="scroll-mt-4" />

      {sociosQ.isPending && <Cargando />}
      {sociosQ.isError && (
        <ErrorCarga
          error={sociosQ.error}
          mensaje="No se pudo cargar el padrón."
          onReintentar={() => void sociosQ.refetch()}
        />
      )}

      {sociosQ.data && sociosVisibles.length > 0 && (
        <p role="status" className="text-sm text-neutral-600">
          {rangoMostrado(filtros.pagina, total, sociosVisibles.length)}
        </p>
      )}

      {/* "Aún no hay socios" sólo si el padrón está vacío de verdad (total 0),
          no por estar en una página sin filas. */}
      {sociosQ.data && total === 0 && (
        filtroActivo || hayFiltros ? (
          <EmptyState
            icon={Search}
            tone="slate"
            title={filtroActivo ? `Sin resultados para "${filtroActivo}"` : "Sin resultados"}
            description="Prueba con otro nombre, RUT o número de socio, o cambia los filtros."
          />
        ) : (
          <EmptyState
            icon={Users}
            title="Aún no hay socios cargados"
            description="Empieza agregando socios uno por uno. El padrón es la base para emitir lecturas, contratos y boletas."
          >
            <Button icon={Plus} onClick={() => setEditing("new")}>
              Agregar primer socio
            </Button>
          </EmptyState>
        )
      )}

      {sociosVisibles.length > 0 && (
        <ul className="space-y-2 md:hidden">
          {sociosVisibles.map((s) => (
            <SocioCard key={s.id} socio={s} origen={origen} onEdit={() => setEditing(s)} />
          ))}
        </ul>
      )}

      {sociosVisibles.length > 0 && (
        <TableWrap label="Socios" className="hidden md:block">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <Th>Socio</Th>
                <Th>RUT</Th>
                <Th>N° / Libro</Th>
                <Th>Contacto</Th>
                <Th>Tipo</Th>
                <Th>Estado</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {sociosVisibles.map((s) => (
                <SocioRow key={s.id} socio={s} origen={origen} onEdit={() => setEditing(s)} />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {paginas > 1 && (
        <nav
          aria-label="Páginas del padrón"
          className="flex flex-wrap items-center justify-between gap-3"
        >
          <Button
            variant="secondary"
            icon={ChevronLeft}
            disabled={filtros.pagina <= 1}
            onClick={() => irAPagina(filtros.pagina - 1)}
          >
            Anterior
          </Button>
          <span className="text-sm text-neutral-700">
            Página {filtros.pagina} de {paginas}
          </span>
          <Button
            variant="secondary"
            iconRight={ChevronRight}
            disabled={filtros.pagina >= paginas}
            onClick={() => irAPagina(filtros.pagina + 1)}
          >
            Siguiente
          </Button>
        </nav>
      )}
    </div>
  );
}

/** Vista móvil del padrón (auditoría A11Y-01). El cambio Socio/Cliente,
 *  que mueve el IVA, queda en Editar, con su confirmación. */
function SocioCard({
  socio,
  origen,
  onEdit,
}: {
  socio: SocioRead;
  origen: OrigenNavegacion;
  onEdit: () => void;
}): JSX.Element {
  const [showPortal, setShowPortal] = useState(false);
  return (
    <li
      className={`rounded-lg border border-neutral-200 bg-white p-3 text-sm ${
        socio.estado === "Activo" ? "" : "opacity-80"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-neutral-900">{socio.nombre}</div>
          <div className="text-xs text-neutral-600">
            {formatRut(socio.rut)}
            {socio.numero_socio ? ` · N° ${socio.numero_socio}` : ""}
            {" · "}
            {socio.es_socio ? "Socio" : "Cliente"}
          </div>
          {socio.direccion && (
            <div className="text-xs text-neutral-500">{socio.direccion}</div>
          )}
        </div>
        <EstadoBadge estado={socio.estado} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1">
        <Link
          to={`/socios/${socio.id}/cuenta-corriente`}
          state={{ desde: origen }}
          className="inline-flex min-h-11 items-center gap-1 font-medium text-brand-700 hover:text-brand-800"
        >
          Cuenta corriente
          <ArrowRight className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" />
        </Link>
        <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
          Editar
        </Button>
        <Button variant="link" size="sm" icon={KeyRound} onClick={() => setShowPortal(true)}>
          Acceso al portal
        </Button>
      </div>
      {showPortal && (
        <PortalSocioModal socio={socio} onClose={() => setShowPortal(false)} />
      )}
    </li>
  );
}

function SocioRow({
  socio,
  origen,
  onEdit,
}: {
  socio: SocioRead;
  origen: OrigenNavegacion;
  onEdit: () => void;
}): JSX.Element {
  const [showPortal, setShowPortal] = useState(false);

  return (
    <tr className={socio.estado === "Activo" ? "hover:bg-neutral-50" : "opacity-70 hover:bg-neutral-50"}>
      <Td className="font-medium text-neutral-900">
        <span className="inline-flex items-center gap-1.5">
          {socio.nombre}
          <AuditInfo
            createdAt={socio.created_at}
            updatedAt={socio.updated_at}
          />
        </span>
        {socio.direccion && (
          <div className="text-xs text-neutral-500 font-normal">
            {socio.direccion}
          </div>
        )}
      </Td>
      <Td className="text-neutral-600">{formatRut(socio.rut)}</Td>
      <Td className="text-neutral-600 text-xs">
        {socio.numero_socio ? <div>N° {socio.numero_socio}</div> : null}
        {socio.numero_libro ? <div>{socio.numero_libro}</div> : null}
        {!socio.numero_socio && !socio.numero_libro && "—"}
      </Td>
      <Td className="text-neutral-600 text-xs">
        {socio.telefono && <div>{socio.telefono}</div>}
        {socio.correo && <div className="text-neutral-500">{socio.correo}</div>}
        {!socio.telefono && !socio.correo && "—"}
      </Td>
      <Td>
        {/* La condición es un dato, no un interruptor (auditoría UX-02):
            cambiarla mueve el IVA de la boleta y el derecho a asamblea, así
            que va en Editar, con su confirmación. */}
        {socio.es_socio ? (
          <span className="text-xs font-medium text-emerald-700">Socio</span>
        ) : (
          <span className="text-xs text-neutral-600">Cliente</span>
        )}
      </Td>
      <Td>
        <EstadoBadge estado={socio.estado} />
      </Td>
      <Td>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="link"
            size="sm"
            icon={Pencil}
            onClick={onEdit}
            title="Editar"
          >
            Editar
          </Button>
          <Button
            variant="link"
            size="sm"
            icon={KeyRound}
            onClick={() => setShowPortal(true)}
            title="Activar, ver o revocar la clave del socio en el portal"
          >
            Acceso al portal
          </Button>
          <Link
            to={`/socios/${socio.id}/cuenta-corriente`}
            state={{ desde: origen }}
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
          >
            Cuenta corriente
            <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
          </Link>
        </div>
      </Td>
      {showPortal && (
        <PortalSocioModal
          socio={socio}
          onClose={() => setShowPortal(false)}
        />
      )}
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form de alta/edición
// ────────────────────────────────────────────────────────────────────────

function SocioForm({
  socio,
  onClose,
  onCreated,
}: {
  socio: SocioRead | null;
  onClose: () => void;
  onCreated: (s: SocioRead) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = socio !== null;

  const [nombre, setNombre] = useState(socio?.nombre ?? "");
  const [rut, setRut] = useState(socio?.rut ?? "");
  const [numeroSocio, setNumeroSocio] = useState(
    socio?.numero_socio != null ? String(socio.numero_socio) : "",
  );
  const [numeroLibro, setNumeroLibro] = useState(socio?.numero_libro ?? "");
  const [telefono, setTelefono] = useState(socio?.telefono ?? "");
  const [correo, setCorreo] = useState(socio?.correo ?? "");
  const [direccion, setDireccion] = useState(socio?.direccion ?? "");
  // En el alta no hay valor por defecto: Socio o Cliente cambia el IVA de la
  // boleta, así que la administrativa lo elige explícitamente.
  const [esSocio, setEsSocio] = useState<boolean | null>(socio?.es_socio ?? null);
  const [estado, setEstado] = useState<EstadoSocio>(socio?.estado ?? "Activo");
  const [fechaNacimiento, setFechaNacimiento] = useState(
    socio?.fecha_nacimiento ?? "",
  );
  const [fechaIngreso, setFechaIngreso] = useState(socio?.fecha_ingreso ?? "");
  const [error, setError] = useState<string | null>(null);
  // Error de validación atado a su campo: se muestra bajo el campo y el
  // foco va ahí, para no tener que buscarlo (auditoría UX-08).
  const [campoError, setCampoError] = useState<{
    campo: "nombre" | "rut" | "calidad" | "numero";
    mensaje: string;
  } | null>(null);
  const limpiarError = (campo: "nombre" | "rut" | "calidad" | "numero"): void => {
    if (campoError?.campo === campo) {
      setCampoError(null);
      setError(null);
    }
  };

  // Cambios sin guardar (auditoría UX-01): se compara contra lo que había al
  // abrir. Cerrar con cambios pregunta antes de descartar.
  const valoresActuales = JSON.stringify([
    nombre, rut, numeroSocio, numeroLibro, telefono, correo, direccion,
    esSocio, estado, fechaNacimiento, fechaIngreso,
  ]);
  const [valoresIniciales] = useState(valoresActuales);
  const hayCambios = valoresActuales !== valoresIniciales;

  const crearMut = useMutation({
    mutationFn: (p: SocioCreatePayload) => crearSocio(p),
    onSuccess: (s) => {
      toast.success(`Socio "${s.nombre}" creado`);
      void qc.invalidateQueries({ queryKey: ["socios"] });
      onCreated(s);
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: SocioUpdatePayload) =>
      actualizarSocio((socio as SocioRead).id, p),
    onSuccess: (s) => {
      toast.success(`Socio "${s.nombre}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["socios"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;
  const { cerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    isPending,
  );

  // Al pasar a Inactivo/Fallecido los contratos NO se tocan: la boleta le
  // sigue saliendo hasta traspasarlos o finalizarlos (auditoría UX-12).
  const contratosQ = useQuery({
    queryKey: ["contratos"],
    queryFn: fetchContratos,
    enabled: editando && estado !== "Activo",
  });
  const contratosVigentes =
    editando && socio
      ? (contratosQ.data ?? []).filter((c) => c.socio_id === socio.id)
      : [];

  // Cambiar Socio ↔ Cliente al editar cambia el IVA de su próxima boleta:
  // se confirma con el dato concreto antes de guardar.
  const [confirmarCalidad, setConfirmarCalidad] = useState<SocioUpdatePayload | null>(
    null,
  );

  function submit(): void {
    setError(null);
    setCampoError(null);
    const fallar = (
      campo: "nombre" | "rut" | "calidad" | "numero",
      mensaje: string,
      idFoco: string,
    ): void => {
      setCampoError({ campo, mensaje });
      setError(mensaje);
      document.getElementById(idFoco)?.focus();
    };
    if (!nombre.trim()) return fallar("nombre", "Escribe el nombre completo", "socio-nombre");
    if (!editando && !rut.trim()) return fallar("rut", "Escribe el RUT", "socio-rut");
    if (!editando) {
      const errRut = errorRut(rut);
      if (errRut) return fallar("rut", errRut, "socio-rut");
    }

    if (esSocio === null) {
      return fallar("calidad", "Indica si es Socio o Cliente", "socio-calidad-socio");
    }

    const num =
      numeroSocio.trim() === "" ? null : Number(numeroSocio);
    if (num !== null && (!Number.isInteger(num) || num <= 0)) {
      return fallar("numero", "El N° de socio debe ser un entero positivo", "socio-numero");
    }

    const payloadComun = {
      nombre: nombre.trim(),
      numero_socio: num,
      numero_libro: numeroLibro.trim() || null,
      telefono: telefono.trim() || null,
      correo: correo.trim() || null,
      direccion: direccion.trim() || null,
      es_socio: esSocio,
      fecha_nacimiento: fechaNacimiento || null,
      fecha_ingreso: fechaIngreso || null,
      estado,
    };

    if (editando) {
      if (socio && esSocio !== socio.es_socio) {
        setConfirmarCalidad(payloadComun);
        return;
      }
      actualizarMut.mutate(payloadComun);
    } else {
      crearMut.mutate({ ...payloadComun, rut: normalizarRut(rut) ?? rut.trim() });
    }
  }

  return (
    <Modal
      open
      onClose={cerrar}
      title={editando ? "Editar socio" : "Nuevo socio"}
      subtitle={editando ? `RUT ${formatRut(socio.rut)}` : "Carga los datos del nuevo socio"}
      icon={Users}
      size="xl"
      footer={
        <>
          {error && (
            <p className="mr-auto text-xs text-rose-700 self-center">{error}</p>
          )}
          <Button variant="secondary" onClick={cerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear socio"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Agrupado por finalidad (auditoría UX-08). La condición va en
            Identificación porque define si lleva N° de socio e IVA. */}
        <fieldset className="space-y-3">
          <legend className="mb-1 text-sm font-semibold text-neutral-900">Identificación</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              id="socio-nombre"
              label="Nombre completo"
              required
              type="text"
              maxLength={128}
              value={nombre}
              onChange={(e) => {
                setNombre(e.target.value);
                limpiarError("nombre");
              }}
              disabled={isPending}
              error={campoError?.campo === "nombre" ? campoError.mensaje : undefined}
            />
            <Input
              id="socio-rut"
              label="RUT"
              required={!editando}
              type="text"
              maxLength={12}
              value={editando ? formatRut(rut) : rut}
              onChange={(e) => {
                setRut(e.target.value);
                limpiarError("rut");
              }}
              // Editando, el RUT es la identidad del socio: se puede leer y
              // copiar, pero no cambiar (un RUT distinto es un alta nueva).
              readOnly={editando}
              disabled={isPending}
              placeholder="12.345.678-9"
              hint={editando ? "No se edita: un RUT distinto es un socio nuevo." : undefined}
              error={campoError?.campo === "rut" ? campoError.mensaje : undefined}
              className={editando ? "bg-neutral-50 text-neutral-700" : undefined}
            />
            <Input
              label="Fecha de nacimiento"
              type="date"
              value={fechaNacimiento}
              onChange={(e) => setFechaNacimiento(e.target.value)}
              disabled={isPending}
            />
          </div>

          <fieldset
            className="space-y-1.5"
            aria-describedby={campoError?.campo === "calidad" ? "socio-calidad-error" : undefined}
          >
            <legend className="text-sm font-medium text-neutral-700">
              Condición
              <span className="text-rose-600" aria-hidden="true">
                {" "}
                *
              </span>
            </legend>
            <label className="flex items-start gap-2 text-sm text-neutral-700">
              <input
                id="socio-calidad-socio"
                type="radio"
                name="calidad-socio"
                className="mt-1"
                checked={esSocio === true}
                onChange={() => {
                  setEsSocio(true);
                  limpiarError("calidad");
                }}
                disabled={isPending}
              />
              <span>
                <strong>Socio</strong>: con derecho a asamblea; su boleta es exenta
                de IVA.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-neutral-700">
              <input
                type="radio"
                name="calidad-socio"
                className="mt-1"
                checked={esSocio === false}
                onChange={() => {
                  setEsSocio(false);
                  limpiarError("calidad");
                }}
                disabled={isPending}
              />
              <span>
                <strong>Cliente</strong>: recibe el servicio sin derecho a asamblea;
                su boleta lleva IVA (19 %).
              </span>
            </label>
            {campoError?.campo === "calidad" && (
              <p id="socio-calidad-error" className="text-xs text-rose-700">
                {campoError.mensaje}
              </p>
            )}
          </fieldset>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-1 text-sm font-semibold text-neutral-900">Contacto</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Teléfono"
              type="tel"
              maxLength={15}
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              disabled={isPending}
              placeholder="+56 9 1234 5678"
            />
            <Input
              label="Correo"
              type="email"
              maxLength={128}
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              disabled={isPending}
            />
          </div>
          <Input
            label="Dirección"
            type="text"
            maxLength={256}
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            disabled={isPending}
          />
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-1 text-sm font-semibold text-neutral-900">
            Datos del comité
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Estado"
              value={estado}
              onChange={(e) => setEstado(e.target.value as EstadoSocio)}
              disabled={isPending}
            >
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
              <option value="Fallecido">Fallecido</option>
            </Select>
            <Input
              id="socio-numero"
              label="N° de socio"
              type="number"
              min={1}
              value={numeroSocio}
              onChange={(e) => {
                setNumeroSocio(e.target.value);
                limpiarError("numero");
              }}
              disabled={isPending}
              placeholder={editando ? "" : "Se asigna solo"}
              hint={
                editando
                  ? undefined
                  : "Déjalo vacío y se asigna el siguiente número (sólo a los Socios; los Clientes no llevan número)."
              }
              error={campoError?.campo === "numero" ? campoError.mensaje : undefined}
            />
            <Input
              label="N° de libro"
              type="text"
              maxLength={50}
              value={numeroLibro}
              onChange={(e) => setNumeroLibro(e.target.value)}
              disabled={isPending}
            />
            <Input
              label="Fecha de ingreso al comité"
              type="date"
              value={fechaIngreso}
              onChange={(e) => setFechaIngreso(e.target.value)}
              disabled={isPending}
            />
          </div>
        </fieldset>

        {editando && estado !== "Activo" && (
          <div
            role="status"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
            <div>
              Marcarlo {estado === "Fallecido" ? "Fallecido" : "Inactivo"} no
              cierra sus contratos: la boleta le sigue saliendo hasta que los
              traspases (herencia, venta) o los finalices en{" "}
              <Link to="/contratos" className="font-medium underline underline-offset-2">
                Contratos
              </Link>
              .
              {editando && contratosQ.data && (
                <div className="mt-1">
                  {contratosVigentes.length === 0 ? (
                    "No tiene contratos vigentes."
                  ) : (
                    <>
                      Tiene {contratosVigentes.length} contrato(s) vigente(s):{" "}
                      {contratosVigentes
                        .map((c) => `arranque ${c.arranque_codigo ?? "—"} (${c.arranque_direccion})`)
                        .join(", ")}
                      .
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {editando && socio && (
          <p className="text-xs text-neutral-500">
            Creado {formatDate(socio.created_at)} · última modificación{" "}
            {formatDate(socio.updated_at)}
          </p>
        )}
      </div>
      {socio && (
        <ConfirmDialog
          open={confirmarCalidad !== null}
          tone="default"
          title={esSocio ? "Promover a Socio" : "Cambiar a Cliente"}
          confirmLabel={esSocio ? "Guardar como Socio" : "Guardar como Cliente"}
          loading={actualizarMut.isPending}
          onCancel={() => setConfirmarCalidad(null)}
          onConfirm={() => {
            if (confirmarCalidad) actualizarMut.mutate(confirmarCalidad);
            setConfirmarCalidad(null);
          }}
          description={
            esSocio ? (
              <>
                <strong>{socio.nombre}</strong> pasa a tener derecho a asamblea y
                desde su próxima boleta queda <strong>exento de IVA</strong>. Este
                cambio no cobra la cuota de incorporación: si corresponde,
                regístrala como ajuste en su cuenta corriente.
              </>
            ) : (
              <>
                <strong>{socio.nombre}</strong> pierde el derecho a asamblea y
                desde su próxima boleta se le cobra <strong>IVA (19 %)</strong>{" "}
                sobre el servicio de agua.
              </>
            )
          }
        />
      )}
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers UI
// ────────────────────────────────────────────────────────────────────────

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <th
      scope="col"
      className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return <td className={`px-4 py-3 text-sm ${className}`}>{children}</td>;
}

function EstadoBadge({ estado }: { estado: EstadoSocio }): JSX.Element {
  // Fallecido es una condición de la persona, no un error: va en neutro
  // (más oscuro que Inactivo para distinguirlos), nunca en rose. Lo que sí
  // requiere acción (contratos vigentes) lo avisa el formulario.
  const map: Record<EstadoSocio, BadgeTone> = {
    Activo: "emerald",
    Inactivo: "slate",
    Fallecido: "slate",
  };
  return (
    <Badge
      tone={map[estado]}
      className={estado === "Fallecido" ? "bg-neutral-200 text-neutral-800" : undefined}
    >
      {estado}
    </Badge>
  );
}
